import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { triggerWeekCalculation } from '../services/schedulingJobService';
import { showError } from '../store/errorDialog/errorDialogSlice';
import { useAppDispatch } from '../store/hooks';
import { getApiErrorMessage } from '../utils/errorMessageUtils';

export interface WeekRange {
  readonly fromWeekNumber: number;
  readonly toWeekNumber: number;
}

export interface UseWeekRangeCalculationResult {
  isDialogOpen: boolean;
  isTriggering: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  confirmCalculation: (range: WeekRange) => Promise<void>;
}

/**
 * Drives the "calculate a range of weeks" dialog: queues one scheduling job per week, in order,
 * since the backend only accepts a single week per trigger.
 *
 * The weeks are queued one at a time and stop at the first refusal — a failure is almost always
 * systemic (permission, scenario gone, backend down), so pressing on would only repeat it. Weeks
 * already queued stay queued and are announced through `onCalculationQueued`, the same as the
 * single-week flow, so the header shows them as calculating at once.
 */
export function useWeekRangeCalculation(
  locationId: string | undefined,
  scenarioId: string | undefined,
  onCalculationQueued?: (weekNumber: number) => void
): UseWeekRangeCalculationResult {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTriggering, setIsTriggering] = useState(false);

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const confirmCalculation = useCallback(
    async ({ fromWeekNumber, toWeekNumber }: WeekRange): Promise<void> => {
      if (!locationId || !scenarioId || isTriggering || fromWeekNumber > toWeekNumber) return;
      setIsTriggering(true);
      const queuedWeekNumbers: number[] = [];
      try {
        for (let weekNumber = fromWeekNumber; weekNumber <= toWeekNumber; weekNumber++) {
          await triggerWeekCalculation(locationId, scenarioId, weekNumber);
          queuedWeekNumbers.push(weekNumber);
        }
      } catch (e) {
        const failedWeekNumber = fromWeekNumber + queuedWeekNumbers.length;
        // With nothing queued the dialog stays open for a retry, as in the single-week flow. Once
        // part of the range is running, retrying the whole range would queue those weeks a second
        // time, so the planner is told where it stopped instead.
        const message =
          queuedWeekNumbers.length === 0
            ? getApiErrorMessage(e, t('gantt-recalculate-week-error'))
            : t('gantt-calculate-weeks-partial-error', { week: failedWeekNumber });
        dispatch(showError({ message }));
      } finally {
        setIsTriggering(false);
      }

      if (queuedWeekNumbers.length === 0) return;
      // Closing first, as in useWeekCalculation: it is correct once the backend has accepted, and
      // must not be skipped by a subscriber that throws.
      setIsDialogOpen(false);
      queuedWeekNumbers.forEach((weekNumber) => onCalculationQueued?.(weekNumber));
    },
    [locationId, scenarioId, isTriggering, dispatch, t, onCalculationQueued]
  );

  return {
    isDialogOpen,
    isTriggering,
    openDialog,
    closeDialog,
    confirmCalculation
  };
}
